import { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { Separator } from '../components/ui/separator';
import { Slider } from '../components/ui/slider';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger
} from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  Palette, Save, Loader2, Plus, X, Sparkles, MessageSquare, Target, AlertTriangle,
  Volume2, RefreshCw, Trash2, Edit, Star, Package, ChevronRight, ChevronDown
} from 'lucide-react';
import api, { productsAPI } from '../api';

const TONE_OPTIONS = [
  { value: 'professional', label: 'Professional', description: 'Formal, business-like communication' },
  { value: 'casual', label: 'Casual', description: 'Relaxed, friendly tone' },
  { value: 'friendly', label: 'Friendly', description: 'Warm and approachable' },
  { value: 'authoritative', label: 'Authoritative', description: 'Expert, confident voice' },
  { value: 'innovative', label: 'Innovative', description: 'Forward-thinking, cutting-edge' },
  { value: 'empathetic', label: 'Empathetic', description: 'Understanding, supportive' },
  { value: 'urgent', label: 'Urgent', description: 'Time-sensitive, action-oriented' },
  { value: 'luxurious', label: 'Luxurious', description: 'Premium, sophisticated' }
];

const CTA_STYLES = [
  { value: 'direct', label: 'Direct', example: 'Buy Now' },
  { value: 'soft', label: 'Soft', example: 'Learn More' },
  { value: 'urgent', label: 'Urgent', example: 'Limited Time Only!' }
];

export const BrandVoiceSettingsPage = () => {
  const { hasRole } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [brandVoices, setBrandVoices] = useState([]);
  const [selectedVoice, setSelectedVoice] = useState(null);
  const [products, setProducts] = useState([]);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [expandedVoice, setExpandedVoice] = useState(null);
  
  const [settings, setSettings] = useState({
    brand_name: '',
    tagline: '',
    mission_statement: '',
    primary_tone: 'professional',
    secondary_tones: [],
    preferred_language: 'en',
    formality_level: 'medium',
    use_contractions: true,
    use_emojis: false,
    key_messages: [],
    keywords_to_include: [],
    words_to_avoid: [],
    competitor_names_to_avoid: [],
    target_audience_description: '',
    audience_pain_points: [],
    audience_desires: [],
    sentence_length_preference: 'medium',
    paragraph_length_preference: 'medium',
    use_bullet_points: true,
    use_statistics: true,
    use_testimonials: true,
    cta_style: 'direct',
    preferred_ctas: [],
    industry: '',
    compliance_notes: '',
    creativity_level: 0.7,
    max_output_length: 500,
    product_id: '',
    is_default: false
  });

  // Input states for adding items
  const [newKeyMessage, setNewKeyMessage] = useState('');
  const [newKeyword, setNewKeyword] = useState('');
  const [newAvoidWord, setNewAvoidWord] = useState('');
  const [newCompetitor, setNewCompetitor] = useState('');
  const [newPainPoint, setNewPainPoint] = useState('');
  const [newDesire, setNewDesire] = useState('');
  const [newCta, setNewCta] = useState('');

  useEffect(() => {
    fetchBrandVoices();
    fetchProducts();
  }, []);

  const fetchBrandVoices = async () => {
    try {
      const res = await api.get('/marketing/brand-voices');
      const voices = res.data.brand_voices || [];
      setBrandVoices(voices);
      
      // If there's a default voice or first voice, select it
      const defaultVoice = voices.find(v => v.is_default) || voices[0];
      if (defaultVoice) {
        setSelectedVoice(defaultVoice);
        setSettings(prev => ({ ...prev, ...defaultVoice }));
        setExpandedVoice(defaultVoice.id);
      }
    } catch (error) {
      console.error('Failed to fetch brand voices:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchProducts = async () => {
    try {
      const res = await productsAPI.getAll();
      setProducts(res.data || []);
    } catch (error) {
      console.error('Failed to fetch products:', error);
    }
  };

  const selectVoice = (voice) => {
    setSelectedVoice(voice);
    setSettings(prev => ({ 
      ...prev, 
      ...voice,
      product_id: voice.product_id || ''
    }));
    setExpandedVoice(voice.id);
  };

  const handleSave = async () => {
    if (!selectedVoice?.id) return;
    
    setSaving(true);
    try {
      await api.put(`/marketing/brand-voice/${selectedVoice.id}`, settings);
      toast.success('Brand voice settings saved');
      fetchBrandVoices();
    } catch (error) {
      console.error('Failed to save settings:', error);
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleCreate = async () => {
    setSaving(true);
    try {
      const createData = {
        ...settings,
        product_id: settings.product_id || null
      };
      const res = await api.post('/marketing/brand-voices', createData);
      toast.success('Brand voice created');
      setShowCreateDialog(false);
      fetchBrandVoices();
      selectVoice(res.data);
    } catch (error) {
      console.error('Failed to create brand voice:', error);
      toast.error(error.response?.data?.detail || 'Failed to create brand voice');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (voiceId) => {
    if (!window.confirm('Are you sure you want to delete this brand voice?')) return;
    
    try {
      await api.delete(`/marketing/brand-voice/${voiceId}`);
      toast.success('Brand voice deleted');
      fetchBrandVoices();
      if (selectedVoice?.id === voiceId) {
        setSelectedVoice(null);
      }
    } catch (error) {
      console.error('Failed to delete brand voice:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete brand voice');
    }
  };

  const handleSetDefault = async (voiceId) => {
    try {
      await api.post(`/marketing/brand-voice/${voiceId}/set-default`);
      toast.success('Default brand voice updated');
      fetchBrandVoices();
    } catch (error) {
      toast.error('Failed to set default');
    }
  };

  const resetForm = () => {
    setSettings({
      brand_name: '',
      tagline: '',
      mission_statement: '',
      primary_tone: 'professional',
      secondary_tones: [],
      preferred_language: 'en',
      formality_level: 'medium',
      use_contractions: true,
      use_emojis: false,
      key_messages: [],
      keywords_to_include: [],
      words_to_avoid: [],
      competitor_names_to_avoid: [],
      target_audience_description: '',
      audience_pain_points: [],
      audience_desires: [],
      sentence_length_preference: 'medium',
      paragraph_length_preference: 'medium',
      use_bullet_points: true,
      use_statistics: true,
      use_testimonials: true,
      cta_style: 'direct',
      preferred_ctas: [],
      industry: '',
      compliance_notes: '',
      creativity_level: 0.7,
      max_output_length: 500,
      product_id: '',
      is_default: false
    });
  };

  const addToList = (field, value, setter) => {
    if (!value.trim()) return;
    if (!settings[field].includes(value.trim())) {
      setSettings(prev => ({
        ...prev,
        [field]: [...prev[field], value.trim()]
      }));
    }
    setter('');
  };

  const removeFromList = (field, value) => {
    setSettings(prev => ({
      ...prev,
      [field]: prev[field].filter(v => v !== value)
    }));
  };

  const toggleSecondaryTone = (tone) => {
    setSettings(prev => ({
      ...prev,
      secondary_tones: prev.secondary_tones.includes(tone)
        ? prev.secondary_tones.filter(t => t !== tone)
        : [...prev.secondary_tones, tone]
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  const canEdit = hasRole(['admin', 'marketing_manager']);

  return (
    <div className="space-y-6 animate-fade-in" data-testid="brand-voice-settings-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Palette className="h-6 w-6" />
            Brand Voice Settings
          </h1>
          <p className="text-muted-foreground">
            Manage multiple brand voices for different products and campaigns
          </p>
        </div>
        <div className="flex items-center gap-2">
          {canEdit && (
            <>
              <Button 
                variant="outline" 
                onClick={() => {
                  resetForm();
                  setShowCreateDialog(true);
                }}
                data-testid="create-brand-voice-btn"
              >
                <Plus className="h-4 w-4 mr-2" />
                New Brand Voice
              </Button>
              {selectedVoice && (
                <Button onClick={handleSave} disabled={saving} data-testid="save-brand-voice-btn">
                  {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                  Save Changes
                </Button>
              )}
            </>
          )}
        </div>
      </div>

      <div className="grid grid-cols-12 gap-6">
        {/* Brand Voices Sidebar */}
        <div className="col-span-12 lg:col-span-3">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm">Brand Voices</CardTitle>
              <CardDescription>Select a voice to edit</CardDescription>
            </CardHeader>
            <CardContent className="space-y-2">
              {brandVoices.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-4">
                  No brand voices yet. Create your first one!
                </p>
              ) : (
                brandVoices.map((voice) => (
                  <div
                    key={voice.id}
                    className={`p-3 border rounded-lg cursor-pointer transition-colors ${
                      selectedVoice?.id === voice.id 
                        ? 'border-primary bg-primary/5' 
                        : 'hover:bg-slate-50'
                    }`}
                    onClick={() => selectVoice(voice)}
                    data-testid={`brand-voice-item-${voice.id}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1 min-w-0">
                        <p className="font-medium truncate">{voice.brand_name || 'Unnamed Voice'}</p>
                        {voice.product_name && (
                          <div className="flex items-center gap-1 text-xs text-muted-foreground">
                            <Package className="h-3 w-3" />
                            {voice.product_name}
                          </div>
                        )}
                      </div>
                      <div className="flex items-center gap-1">
                        {voice.is_default && (
                          <Badge variant="outline" className="text-xs">
                            <Star className="h-3 w-3 mr-1 fill-yellow-400 text-yellow-400" />
                            Default
                          </Badge>
                        )}
                      </div>
                    </div>
                    {canEdit && selectedVoice?.id === voice.id && (
                      <div className="flex items-center gap-1 mt-2 pt-2 border-t">
                        {!voice.is_default && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleSetDefault(voice.id);
                            }}
                            title="Set as default"
                          >
                            <Star className="h-3 w-3" />
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDelete(voice.id);
                          }}
                          title="Delete"
                          disabled={voice.is_default || brandVoices.length <= 1}
                        >
                          <Trash2 className="h-3 w-3 text-destructive" />
                        </Button>
                      </div>
                    )}
                  </div>
                ))
              )}
            </CardContent>
          </Card>
        </div>

        {/* Settings Editor */}
        <div className="col-span-12 lg:col-span-9">
          {selectedVoice ? (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Brand Identity */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Sparkles className="h-5 w-5" />
                    Brand Identity
                  </CardTitle>
                  <CardDescription>Core brand information for AI context</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label>Brand Voice Name *</Label>
                    <Input
                      value={settings.brand_name}
                      onChange={(e) => setSettings(prev => ({ ...prev, brand_name: e.target.value }))}
                      placeholder="e.g., Enterprise Brand, Consumer Brand"
                      disabled={!canEdit}
                      data-testid="brand-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Linked Product</Label>
                    <Select 
                      value={settings.product_id || "none"} 
                      onValueChange={(v) => setSettings(prev => ({ ...prev, product_id: v === "none" ? "" : v }))}
                      disabled={!canEdit}
                    >
                      <SelectTrigger data-testid="product-select">
                        <SelectValue placeholder="Select a product (optional)" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">No product (General voice)</SelectItem>
                        {products.map(product => (
                          <SelectItem key={product.id} value={product.id}>
                            {product.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground">
                      Link this voice to a product for automatic selection in campaigns
                    </p>
                  </div>
                  <div className="space-y-2">
                    <Label>Tagline</Label>
                    <Input
                      value={settings.tagline}
                      onChange={(e) => setSettings(prev => ({ ...prev, tagline: e.target.value }))}
                      placeholder="Your brand tagline or slogan"
                      disabled={!canEdit}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Mission Statement</Label>
                    <Textarea
                      value={settings.mission_statement}
                      onChange={(e) => setSettings(prev => ({ ...prev, mission_statement: e.target.value }))}
                      placeholder="What does your brand stand for?"
                      rows={3}
                      disabled={!canEdit}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Industry</Label>
                    <Input
                      value={settings.industry}
                      onChange={(e) => setSettings(prev => ({ ...prev, industry: e.target.value }))}
                      placeholder="e.g., Technology, Healthcare, Finance"
                      disabled={!canEdit}
                    />
                  </div>
                </CardContent>
              </Card>

              {/* Voice & Tone */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Volume2 className="h-5 w-5" />
                    Voice & Tone
                  </CardTitle>
                  <CardDescription>How your brand sounds</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label>Primary Tone</Label>
                    <Select
                      value={settings.primary_tone}
                      onValueChange={(v) => setSettings(prev => ({ ...prev, primary_tone: v }))}
                      disabled={!canEdit}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {TONE_OPTIONS.map(tone => (
                          <SelectItem key={tone.value} value={tone.value}>
                            <div>
                              <span className="font-medium">{tone.label}</span>
                              <span className="text-xs text-muted-foreground ml-2">- {tone.description}</span>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label>Secondary Tones (select multiple)</Label>
                    <div className="flex flex-wrap gap-2">
                      {TONE_OPTIONS.filter(t => t.value !== settings.primary_tone).map(tone => (
                        <Badge
                          key={tone.value}
                          variant={settings.secondary_tones.includes(tone.value) ? 'default' : 'outline'}
                          className="cursor-pointer"
                          onClick={() => canEdit && toggleSecondaryTone(tone.value)}
                        >
                          {tone.label}
                        </Badge>
                      ))}
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Formality Level</Label>
                    <Select
                      value={settings.formality_level}
                      onValueChange={(v) => setSettings(prev => ({ ...prev, formality_level: v }))}
                      disabled={!canEdit}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="casual">Casual - Relaxed, conversational</SelectItem>
                        <SelectItem value="medium">Medium - Balanced, professional yet approachable</SelectItem>
                        <SelectItem value="formal">Formal - Business, corporate</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="flex items-center justify-between">
                      <Label>Use Contractions</Label>
                      <Switch
                        checked={settings.use_contractions}
                        onCheckedChange={(c) => setSettings(prev => ({ ...prev, use_contractions: c }))}
                        disabled={!canEdit}
                      />
                    </div>
                    <div className="flex items-center justify-between">
                      <Label>Use Emojis</Label>
                      <Switch
                        checked={settings.use_emojis}
                        onCheckedChange={(c) => setSettings(prev => ({ ...prev, use_emojis: c }))}
                        disabled={!canEdit}
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Key Messages */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <MessageSquare className="h-5 w-5" />
                    Key Messages
                  </CardTitle>
                  <CardDescription>Core messages to emphasize in all content</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex gap-2">
                    <Input
                      value={newKeyMessage}
                      onChange={(e) => setNewKeyMessage(e.target.value)}
                      placeholder="Add a key message..."
                      onKeyPress={(e) => e.key === 'Enter' && addToList('key_messages', newKeyMessage, setNewKeyMessage)}
                      disabled={!canEdit}
                    />
                    <Button variant="outline" size="icon" onClick={() => addToList('key_messages', newKeyMessage, setNewKeyMessage)} disabled={!canEdit}>
                      <Plus className="h-4 w-4" />
                    </Button>
                  </div>
                  <div className="space-y-2">
                    {settings.key_messages.map((msg, idx) => (
                      <div key={idx} className="flex items-center justify-between p-2 bg-slate-50 rounded">
                        <span className="text-sm">{msg}</span>
                        {canEdit && (
                          <Button variant="ghost" size="icon" onClick={() => removeFromList('key_messages', msg)}>
                            <X className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    ))}
                  </div>

                  <Separator />

                  <div className="space-y-2">
                    <Label>Keywords to Include (SEO/Brand)</Label>
                    <div className="flex gap-2">
                      <Input
                        value={newKeyword}
                        onChange={(e) => setNewKeyword(e.target.value)}
                        placeholder="Add keyword..."
                        onKeyPress={(e) => e.key === 'Enter' && addToList('keywords_to_include', newKeyword, setNewKeyword)}
                        disabled={!canEdit}
                      />
                      <Button variant="outline" size="icon" onClick={() => addToList('keywords_to_include', newKeyword, setNewKeyword)} disabled={!canEdit}>
                        <Plus className="h-4 w-4" />
                      </Button>
                    </div>
                    <div className="flex flex-wrap gap-1">
                      {settings.keywords_to_include.map((kw, idx) => (
                        <Badge key={idx} variant="secondary" className="pr-1">
                          {kw}
                          {canEdit && (
                            <Button variant="ghost" size="icon" className="h-4 w-4 ml-1" onClick={() => removeFromList('keywords_to_include', kw)}>
                              <X className="h-3 w-3" />
                            </Button>
                          )}
                        </Badge>
                      ))}
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Words to Avoid */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-orange-600">
                    <AlertTriangle className="h-5 w-5" />
                    Words & Phrases to Avoid
                  </CardTitle>
                  <CardDescription>Content the AI should never generate</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label>Banned Words/Phrases</Label>
                    <div className="flex gap-2">
                      <Input
                        value={newAvoidWord}
                        onChange={(e) => setNewAvoidWord(e.target.value)}
                        placeholder="Add word to avoid..."
                        onKeyPress={(e) => e.key === 'Enter' && addToList('words_to_avoid', newAvoidWord, setNewAvoidWord)}
                        disabled={!canEdit}
                      />
                      <Button variant="outline" size="icon" onClick={() => addToList('words_to_avoid', newAvoidWord, setNewAvoidWord)} disabled={!canEdit}>
                        <Plus className="h-4 w-4" />
                      </Button>
                    </div>
                    <div className="flex flex-wrap gap-1">
                      {settings.words_to_avoid.map((word, idx) => (
                        <Badge key={idx} variant="destructive" className="pr-1">
                          {word}
                          {canEdit && (
                            <Button variant="ghost" size="icon" className="h-4 w-4 ml-1" onClick={() => removeFromList('words_to_avoid', word)}>
                              <X className="h-3 w-3" />
                            </Button>
                          )}
                        </Badge>
                      ))}
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Competitor Names to Avoid</Label>
                    <div className="flex gap-2">
                      <Input
                        value={newCompetitor}
                        onChange={(e) => setNewCompetitor(e.target.value)}
                        placeholder="Add competitor name..."
                        onKeyPress={(e) => e.key === 'Enter' && addToList('competitor_names_to_avoid', newCompetitor, setNewCompetitor)}
                        disabled={!canEdit}
                      />
                      <Button variant="outline" size="icon" onClick={() => addToList('competitor_names_to_avoid', newCompetitor, setNewCompetitor)} disabled={!canEdit}>
                        <Plus className="h-4 w-4" />
                      </Button>
                    </div>
                    <div className="flex flex-wrap gap-1">
                      {settings.competitor_names_to_avoid.map((comp, idx) => (
                        <Badge key={idx} variant="outline" className="border-orange-300 text-orange-700 pr-1">
                          {comp}
                          {canEdit && (
                            <Button variant="ghost" size="icon" className="h-4 w-4 ml-1" onClick={() => removeFromList('competitor_names_to_avoid', comp)}>
                              <X className="h-3 w-3" />
                            </Button>
                          )}
                        </Badge>
                      ))}
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Target Audience */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Target className="h-5 w-5" />
                    Target Audience
                  </CardTitle>
                  <CardDescription>Who you&apos;re speaking to</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label>Audience Description</Label>
                    <Textarea
                      value={settings.target_audience_description}
                      onChange={(e) => setSettings(prev => ({ ...prev, target_audience_description: e.target.value }))}
                      placeholder="Describe your ideal customer..."
                      rows={2}
                      disabled={!canEdit}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Pain Points</Label>
                    <div className="flex gap-2">
                      <Input
                        value={newPainPoint}
                        onChange={(e) => setNewPainPoint(e.target.value)}
                        placeholder="Add pain point..."
                        onKeyPress={(e) => e.key === 'Enter' && addToList('audience_pain_points', newPainPoint, setNewPainPoint)}
                        disabled={!canEdit}
                      />
                      <Button variant="outline" size="icon" onClick={() => addToList('audience_pain_points', newPainPoint, setNewPainPoint)} disabled={!canEdit}>
                        <Plus className="h-4 w-4" />
                      </Button>
                    </div>
                    <div className="space-y-1">
                      {settings.audience_pain_points.map((pp, idx) => (
                        <div key={idx} className="flex items-center justify-between p-2 bg-red-50 rounded text-sm">
                          <span>• {pp}</span>
                          {canEdit && (
                            <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => removeFromList('audience_pain_points', pp)}>
                              <X className="h-3 w-3" />
                            </Button>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Desires & Goals</Label>
                    <div className="flex gap-2">
                      <Input
                        value={newDesire}
                        onChange={(e) => setNewDesire(e.target.value)}
                        placeholder="Add desire..."
                        onKeyPress={(e) => e.key === 'Enter' && addToList('audience_desires', newDesire, setNewDesire)}
                        disabled={!canEdit}
                      />
                      <Button variant="outline" size="icon" onClick={() => addToList('audience_desires', newDesire, setNewDesire)} disabled={!canEdit}>
                        <Plus className="h-4 w-4" />
                      </Button>
                    </div>
                    <div className="space-y-1">
                      {settings.audience_desires.map((d, idx) => (
                        <div key={idx} className="flex items-center justify-between p-2 bg-green-50 rounded text-sm">
                          <span>• {d}</span>
                          {canEdit && (
                            <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => removeFromList('audience_desires', d)}>
                              <X className="h-3 w-3" />
                            </Button>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* AI Settings */}
              <Card className="lg:col-span-2">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Sparkles className="h-5 w-5" />
                    AI Generation Settings
                  </CardTitle>
                  <CardDescription>Fine-tune AI output parameters</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="grid grid-cols-2 gap-6">
                    <div className="space-y-4">
                      <div className="space-y-2">
                        <div className="flex justify-between">
                          <Label>Creativity Level</Label>
                          <span className="text-sm text-muted-foreground">{Math.round(settings.creativity_level * 100)}%</span>
                        </div>
                        <Slider
                          value={[settings.creativity_level * 100]}
                          onValueChange={([v]) => setSettings(prev => ({ ...prev, creativity_level: v / 100 }))}
                          max={100}
                          step={5}
                          disabled={!canEdit}
                        />
                        <p className="text-xs text-muted-foreground">
                          Lower = more consistent, Higher = more creative/varied
                        </p>
                      </div>

                      <div className="space-y-2">
                        <Label>Max Output Length</Label>
                        <Input
                          type="number"
                          value={settings.max_output_length}
                          onChange={(e) => setSettings(prev => ({ ...prev, max_output_length: parseInt(e.target.value) || 500 }))}
                          disabled={!canEdit}
                        />
                        <p className="text-xs text-muted-foreground">Default maximum characters for generated content</p>
                      </div>
                    </div>

                    <div className="space-y-4">
                      <div className="space-y-2">
                        <Label>CTA Style</Label>
                        <Select
                          value={settings.cta_style}
                          onValueChange={(v) => setSettings(prev => ({ ...prev, cta_style: v }))}
                          disabled={!canEdit}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {CTA_STYLES.map(style => (
                              <SelectItem key={style.value} value={style.value}>
                                {style.label} (e.g., &quot;{style.example}&quot;)
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="grid grid-cols-3 gap-4">
                        <div className="flex items-center justify-between">
                          <Label className="text-sm">Bullet Points</Label>
                          <Switch
                            checked={settings.use_bullet_points}
                            onCheckedChange={(c) => setSettings(prev => ({ ...prev, use_bullet_points: c }))}
                            disabled={!canEdit}
                          />
                        </div>
                        <div className="flex items-center justify-between">
                          <Label className="text-sm">Statistics</Label>
                          <Switch
                            checked={settings.use_statistics}
                            onCheckedChange={(c) => setSettings(prev => ({ ...prev, use_statistics: c }))}
                            disabled={!canEdit}
                          />
                        </div>
                        <div className="flex items-center justify-between">
                          <Label className="text-sm">Testimonials</Label>
                          <Switch
                            checked={settings.use_testimonials}
                            onCheckedChange={(c) => setSettings(prev => ({ ...prev, use_testimonials: c }))}
                            disabled={!canEdit}
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Compliance Notes</Label>
                    <Textarea
                      value={settings.compliance_notes}
                      onChange={(e) => setSettings(prev => ({ ...prev, compliance_notes: e.target.value }))}
                      placeholder="Any regulatory or legal requirements for content..."
                      rows={2}
                      disabled={!canEdit}
                    />
                  </div>
                </CardContent>
              </Card>
            </div>
          ) : (
            <Card className="flex items-center justify-center min-h-[400px]">
              <CardContent className="text-center">
                <Palette className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <p className="text-lg font-medium">No Brand Voice Selected</p>
                <p className="text-muted-foreground mb-4">Select a brand voice from the sidebar or create a new one</p>
                {canEdit && (
                  <Button onClick={() => {
                    resetForm();
                    setShowCreateDialog(true);
                  }}>
                    <Plus className="h-4 w-4 mr-2" />
                    Create Brand Voice
                  </Button>
                )}
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Create Brand Voice Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Create New Brand Voice</DialogTitle>
            <DialogDescription>
              Create a new brand voice for a specific product or as a general template
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Brand Voice Name *</Label>
              <Input
                value={settings.brand_name}
                onChange={(e) => setSettings(prev => ({ ...prev, brand_name: e.target.value }))}
                placeholder="e.g., Enterprise Product Voice, Consumer Brand"
                data-testid="create-brand-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Link to Product (Optional)</Label>
              <Select 
                value={settings.product_id || "none"} 
                onValueChange={(v) => setSettings(prev => ({ ...prev, product_id: v === "none" ? "" : v }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select a product" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No product - General voice</SelectItem>
                  {products.map(product => (
                    <SelectItem key={product.id} value={product.id}>
                      {product.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                Link to a product so this voice is automatically used for that product&apos;s campaigns
              </p>
            </div>
            <div className="space-y-2">
              <Label>Primary Tone</Label>
              <Select
                value={settings.primary_tone}
                onValueChange={(v) => setSettings(prev => ({ ...prev, primary_tone: v }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TONE_OPTIONS.map(tone => (
                    <SelectItem key={tone.value} value={tone.value}>
                      {tone.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center justify-between">
              <div>
                <Label>Set as Default</Label>
                <p className="text-xs text-muted-foreground">
                  Use when no product-specific voice exists
                </p>
              </div>
              <Switch
                checked={settings.is_default}
                onCheckedChange={(c) => setSettings(prev => ({ ...prev, is_default: c }))}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>Cancel</Button>
            <Button 
              onClick={handleCreate} 
              disabled={!settings.brand_name || saving}
              data-testid="create-brand-voice-submit"
            >
              {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Plus className="h-4 w-4 mr-2" />}
              Create
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BrandVoiceSettingsPage;
